<script setup>
import { getQueryParamsFromUrl } from "@/utils/api-drive-params";
import productListUtils from "@/utils/product-list";

import { useContainerPushAbonnementLad } from "@/containers/mixin/push-abonnement-lad-mixin";

import universePageCodes from "@/utils/universe-page-codes";
import pageMarronnierCodes from "@/utils/page-marronnier-codes";
import contentTypes from "@/utils/product-list-content-types";
import { useContainerProductList } from "@/containers/mixin/product-list.mixin";
import { loadPageWithLoader } from "@/utils/webpack-helper";
import { useContextStore } from "@/store/context";
import { useProductsStore } from "@/store/products";
import { useMagasinStore } from "@/store/magasin";
import { useTrackingDP } from "@/mixins/tracking/page-descente-produits";

const reloadOnMounted = process.server;

const {
  productsList,
  sortCriteria,
  appliedFilters,
  seoHead,
  contentType,
  firstFilter,
  title,
  breadcrumbs,
  slug,
  productsListMode,
  isLoading,
  productsCount,
  pushZones,
  boiteLiens
} = storeToRefs(useProductsStore());
const { params, path, query } = useRoute();
const { id: magasinId } = storeToRefs(useMagasinStore());
const { fetchFiltredProductList } = useProductsStore();
const { layout, requestTime, from } = storeToRefs(useContextStore());
const { updateLoadingStatus, fetchProducts } = useProductsStore();
const {
  getAxiosErrorFromAsyncData,
  getErrorStatus,
  getErrorMessage,
  isPageIndexError
} = useAxiosHelper();

const { trackDP, emitEventPartenaires } = useTrackingDP();

const app = useNuxtApp();

const unsubPage = app.hook("page:finish", () => {
  updateLoadingStatus(false);
});

const component = computed(() => {
  switch (contentType.value) {
    case universePageCodes.UNIVERS:
      return loadPageWithLoader(
        lazyComponents[`/components/pages/universe/${layout.value}.vue`]
      );
    case universePageCodes.MARQUE_LISTING:
      return loadPageWithLoader(() =>
        import("@/components/pages/marques/listing")
      );
    case universePageCodes.MARQUE_LANDING:
      return loadPageWithLoader(() =>
        import("@/components/pages/marques/landing")
      );
    case pageMarronnierCodes.MARRONNIERS:
      return loadPageWithLoader(
        productsList.value && productsList.value.length > 0
          ? lazyComponents[`/components/pages/product-list/${layout.value}.vue`]
          : lazyComponents[`/components/pages/marronniers/${layout.value}.vue`]
      );
    case contentTypes.CKDO:
      return loadPageWithLoader(() => import("@/components/pages/carte-kdo"));
    default:
      return loadPageWithLoader(
        lazyComponents[`/components/pages/product-list/${layout.value}.vue`]
      );
  }
});

const pageTitle = computed(() => {
  if (seoHead.value) {
    if (contentType.value === universePageCodes.UNIVERS) {
      return seoHead.value.title + " | Drive & Livraison à domicile - Cora";
    }
    if (appliedFilters.value.length > 0) {
      if (appliedFilters.value[0].code === "NW-13603-marques") {
        return seoHead.value.title < 20
          ? seoHead.value.title +
              " | Découvrez les produits " +
              firstFilter.value +
              " - Cora"
          : seoHead.value.title +
              " | Les produits " +
              firstFilter.value +
              " - Cora";
      } else {
        return seoHead.value.title < 20
          ? seoHead.value.title +
              " | Découvrez nos produits " +
              firstFilter.value +
              " - Cora"
          : seoHead.value.title +
              " | Nos produits " +
              firstFilter.value +
              " - Cora";
      }
    } else {
      return seoHead.value.title + " - Cora";
    }
  }
});

const { isPro, fetchFiltredProducts } = useContainerProductList(
  productsList.value,
  sortCriteria.value,
  appliedFilters.value
);

const { showAboLad, showAboLadRenouvellement } =
  useContainerPushAbonnementLad();

onBeforeUnmount(unsubPage);

onMounted(async () => {
  if (from.value?.name === "article-id-slug" && from.value?.params?.id) {
    setTimeout(() => {
      productListUtils.scrollToProductId(from.value.params.id);
    }, 100);
  } else {
    window.scrollTo(0, 0);
  }

  if (typeof window.cora.partenaires.products.onListLoaded !== "undefined") {
    window.cora.partenaires.products.onListLoaded({
      page: 1,
      perPage: productsCount.value,
      sort: null
    });
  }

  trackDP();

  if (
    reloadOnMounted &&
    Date.now() - requestTime.value >
      useRuntimeConfig().public.API_DRIVE_CACHE_RELOAD_DELAY_DP
  ) {
    try {
      await fetchProducts(parameters, queryParameters);
    } catch (err) {
      // no update
    }
  }
});

const conf = useRuntimeConfig();
useHead(() => {
  if (!seoHead.value) {
    return {};
  }
  const canonicalUrl = seoHead.value.link.find(
    (link) => link.rel === "canonical"
  ).href;
  let noIndex = false;
  for (const filter of appliedFilters.value) {
    if (filterSlug(canonicalUrl).indexOf(filterSlug(filter.valeur)) === -1) {
      noIndex = true;
      break;
    }
  }
  return {
    title: pageTitle.value,
    meta: [
      ...seoHead.value.meta,
      ...(appliedFilters.value.length > 1 || noIndex
        ? [
            {
              name: "robots",
              content: "noindex"
            }
          ]
        : []),
      {
        name: "apple-itunes-app",
        content: `app-id=${conf.public.IOS_CORADRIVE_APP_STORE_ID}`
      }
    ],
    link: seoHead.value.link
  };
});

definePageMeta({
  key: (route) => {
    const params = [];
    if (route.query && route.query.sortby) {
      params.push(route.query.sortby);
    }
    return route.path + params.join("-");
  }
});

const parameters = {
  shopId: magasinId.value,
  categoryId: params.id,
  isPro: isPro.value
};
const queryParameters = getQueryParamsFromUrl(params, query);

const { error } = await useAsyncData(() => {
  return fetchFiltredProductList({
    ...parameters,
    ...queryParameters
  });
});

const responseError = getAxiosErrorFromAsyncData(error);

if (responseError) {
  if (isPageIndexError(responseError)) {
    navigateToInternal(path, { replace: true, redirectCode: 301 });
  } else {
    throw showError({
      statusCode: getErrorStatus(responseError),
      statusMessage: getErrorMessage(responseError)
    });
  }
}

// Cas mauvais slug
if (
  process.server &&
  slug.value &&
  filterSlug(path) !== filterSlug(slug.value)
) {
  await navigateToInternal(filterSlug(slug.value), {
    replace: true,
    redirectCode: 301
  });
}

// cas de descente avec 0 produits
if (productsCount.value == 0 && contentType.value === "PageDescente") {
  if (breadcrumbs.value && breadcrumbs.value.length >= 2) {
    await navigateToInternal(
      breadcrumbs.value[breadcrumbs.value.length - 2].slug,
      {
        replace: true,
        redirectCode: 301
      }
    );
  }
}

emitEventPartenaires();
</script>

<template>
  <component
    :is="component"
    :sort-criteria="sortCriteria"
    :applied-filters="appliedFilters"
    :is-loading="isLoading"
    :products-list-mode="productsListMode"
    :push-zones="pushZones"
    :show-abo-lad="showAboLad"
    :show-abo-lad-renouvellement="showAboLadRenouvellement"
    :is-pro="isPro"
    :boite-liens="boiteLiens"
    @fetchFiltredProducts="fetchFiltredProducts"
  />
</template>
